import React, { useState, useEffect } from "react";
import {
  Link2, CheckCircle2, KeyRound, Ban, ShieldAlert, ChevronDown, Mail, Calendar,
  MessageCircle, Share2, Plane, Cpu,
} from "lucide-react";
import { getConnectors } from "../../lib/api";
import { SectionHeader } from "./Sections";

const CAT_ICON = {
  "AI Models": Cpu, Email: Mail, Calendar: Calendar, Messaging: MessageCircle,
  Social: Share2, Travel: Plane,
};

const STATUS = {
  connected: { label: "Connected & live", color: "#10B981", icon: CheckCircle2 },
  needs_setup: { label: "Available · needs credentials", color: "#E2B857", icon: KeyRound },
  restricted: { label: "Restricted by platform", color: "#F59E0B", icon: ShieldAlert },
  unavailable: { label: "Not available via official API", color: "#F43F5E", icon: Ban },
};

export default function ConnectorsHub() {
  const [connectors, setConnectors] = useState([]);
  const [open, setOpen] = useState(null);
  useEffect(() => { getConnectors().then(setConnectors).catch(() => {}); }, []);

  const cats = Array.from(new Set(connectors.map((c) => c.category)));

  return (
    <div>
      <SectionHeader overline="Connector Architecture" title="Connections"
        desc="An honest map of every channel Aria can operate. What is live, what needs your credentials, and what no official API permits — never simulated." />

      <div className="space-y-10">
        {cats.map((cat) => {
          const Icon = CAT_ICON[cat] || Link2;
          const items = connectors.filter((c) => c.category === cat);
          return (
            <div key={cat} className="fade-up">
              <div className="flex items-center gap-2.5 mb-4">
                <Icon size={16} className="text-amber-400/80" />
                <span className="overline">{cat}</span>
                <div className="hairline flex-1 ml-2" />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {items.map((c) => {
                  const s = STATUS[c.status] || STATUS.needs_setup;
                  const SIcon = s.icon;
                  const expanded = open === c.key;
                  return (
                    <div key={c.key} className="glass-card p-6" data-testid={`connector-${c.key}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h4 className="font-display text-xl font-medium text-amber-50/95">{c.name}</h4>
                          <div className="flex items-center gap-1.5 mt-1.5 text-[12px] font-medium" style={{ color: s.color }}>
                            <SIcon size={13} /> {s.label}
                          </div>
                        </div>
                        <span className="w-2.5 h-2.5 rounded-full mt-1" style={{ background: s.color, boxShadow: `0 0 12px ${s.color}` }} />
                      </div>
                      <p className="text-sm text-slate-400/90 mt-3 leading-relaxed font-light">{c.note}</p>
                      {c.requires?.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          {c.requires.map((r) => (
                            <span key={r} className="text-[10px] font-mono px-2 py-0.5 rounded-md bg-amber-500/10 text-amber-300/80 border border-amber-500/15">{r}</span>
                          ))}
                        </div>
                      )}
                      {c.how && (
                        <>
                          <button onClick={() => setOpen(expanded ? null : c.key)} className="mt-4 flex items-center gap-1 text-[12px] text-amber-400/80 hover:text-amber-300 transition" data-testid={`connector-how-${c.key}`}>
                            How to connect <ChevronDown size={13} className={`transition ${expanded ? "rotate-180" : ""}`} />
                          </button>
                          {expanded && <p className="mt-2 text-[13px] text-slate-300/80 leading-relaxed border-l border-amber-500/20 pl-3">{c.how}</p>}
                        </>
                      )}
                      {c.status === "needs_setup" && (
                        <button className="mt-4 ghost-btn rounded-full px-4 py-1.5 text-xs" data-testid={`connect-${c.key}`}>
                          Share credentials in chat to activate
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
